/**
 * Parent-controlled settings — spec §3.5.
 *
 * Surfaces: speech (voice + rate), audio mute, skip-this-week streak
 * freeze, destructive Reset stats / Clear words actions, and
 * "Sign out this device".
 *
 * Save model:
 *  - Audio mute + skip-this-week toggles save immediately — parents
 *    expect "tap to mute" to mute instantly, and the streak freeze
 *    is consequential enough to want the visible state to match the
 *    server state with no in-between.
 *  - Speech voice + rate stage in local state and persist via the
 *    explicit Save button. Avoids a flurry of PUTs while dragging the
 *    rate slider.
 *  - Reset stats / Clear words sit behind a confirmation modal each
 *    and call the action only after explicit OK — spec §3.5 says
 *    "strongly confirmation-gated."
 *
 * Voice list: `speechSynthesis.getVoices()` is populated async on first
 * call in Chrome/Safari, so we listen for `voiceschanged` to fill the
 * dropdown when it's ready. Falls back gracefully to "device default" if
 * the list is empty.
 */
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { setSoundMuted } from "@/lib/sounds";
import { resolveVoice } from "@/lib/speech";
import type { Settings, StreakState } from "@/types/data";
import "./ParentSettings.css";

const RATE_MIN = 0.5;
const RATE_MAX = 1.5;
const RATE_STEP = 0.05;

export function ParentSettings() {
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [settings, setSettings] = useState<Settings | null>(null);
  const [draftVoice, setDraftVoice] = useState<string | null>(null);
  const [draftRate, setDraftRate] = useState(0.9);

  const [streak, setStreak] = useState<StreakState | null>(null);

  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
  const [resetStatsConfirmOpen, setResetStatsConfirmOpen] = useState(false);
  const [clearWordsConfirmOpen, setClearWordsConfirmOpen] = useState(false);
  // "running" prevents double-tapping the confirm button while the
  // POST is in flight; cleared on completion regardless of outcome.
  const [destructiveRunning, setDestructiveRunning] = useState<
    null | "reset-stats" | "clear-words"
  >(null);
  const [destructiveDone, setDestructiveDone] = useState<
    null | "reset-stats" | "clear-words"
  >(null);

  const load = useCallback(async () => {
    const [settingsRes, streakRes] = await Promise.all([
      api.getSettings(),
      api.getStreak(),
    ]);
    if (settingsRes.status !== 200 || !settingsRes.body) {
      setLoadError("Couldn't load settings.");
      return;
    }
    setLoadError(null);
    setSettings(settingsRes.body);
    // Seed the dropdown with what the resolver would actually pick on
    // this device — not the literal stored URI. Otherwise on iPad the
    // dropdown reads "Device default" while audio plays Kate, which
    // looks broken even though it's working. If the resolver can't
    // pick anything (no voices loaded yet, no English voices), fall
    // back to the raw stored value.
    const available = window.speechSynthesis?.getVoices?.() ?? [];
    const resolved = resolveVoice(available, settingsRes.body.speechVoice);
    setDraftVoice(resolved?.voiceURI ?? settingsRes.body.speechVoice);
    setDraftRate(settingsRes.body.speechRate);
    // Streak read powers the skip-this-week toggle. A failure here
    // shouldn't block the rest of the settings page — just leave the
    // toggle in an "unknown" state and the user can retry by toggling.
    if (streakRes.status === 200 && streakRes.body) {
      setStreak(streakRes.body);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await load();
      setLoading(false);
    })();
  }, [load]);

  // Populate the voice list. Some browsers fire 'voiceschanged' once the
  // list is ready; others have it on first call.
  useEffect(() => {
    function refresh() {
      const all = window.speechSynthesis?.getVoices?.() ?? [];
      const english = all.filter((v) => v.lang.toLowerCase().startsWith("en"));
      setVoices(english.length > 0 ? english : all);
    }
    refresh();
    const synth = window.speechSynthesis;
    if (synth && "addEventListener" in synth) {
      synth.addEventListener("voiceschanged", refresh);
      return () => synth.removeEventListener("voiceschanged", refresh);
    }
    return undefined;
  }, []);

  // Catch-up resolve. If voices arrive *after* load() seeded the
  // dropdown (the typical iPad path — voiceschanged fires late),
  // re-run the resolver against the now-populated list so the
  // dropdown reflects what the audio is actually using.
  //
  // Two guards against overwriting an explicit user pick:
  //   - draftVoice === null means the user explicitly chose "Device
  //     default" from the dropdown — leave it alone.
  //   - if draftVoice is already in the available list, no resolution
  //     needed; the user's pick is honoured.
  useEffect(() => {
    if (!settings) return;
    if (voices.length === 0) return;
    if (draftVoice === null) return;
    const draftInList = voices.some((v) => v.voiceURI === draftVoice);
    if (draftInList) return;
    const resolved = resolveVoice(voices, settings.speechVoice);
    if (resolved) setDraftVoice(resolved.voiceURI);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voices, settings]);

  async function saveDrafts() {
    if (settings === null) return;
    setSaving(true);
    setSaveError(null);
    const { status, body } = await api.updateSettings({
      speechVoice: draftVoice,
      speechRate: draftRate,
    });
    setSaving(false);
    if (status === 200 && body !== null && "speechRate" in body) {
      const updated = body as Settings;
      setSettings(updated);
      setSavedAt(Date.now());
      return;
    }
    setSaveError("Couldn't save. Try again.");
  }

  async function setAudioEnabled(enabled: boolean) {
    if (settings === null) return;
    // Optimistic update so the UI feels instant.
    const previous = settings;
    setSettings({ ...settings, audioEnabled: enabled });
    // Mirror the change into the sound module immediately. SessionContext
    // also syncs this on settings load, but a parent toggling mute in the
    // parent area won't have an active session to trigger that — and even
    // if they did, the round-trip would feel laggy here.
    setSoundMuted(!enabled);
    const { status, body } = await api.updateSettings({ audioEnabled: enabled });
    if (status !== 200 || body === null || !("audioEnabled" in body)) {
      setSettings(previous);
      setSoundMuted(!previous.audioEnabled);
      setSaveError("Couldn't update the mute setting.");
      return;
    }
    setSettings(body as Settings);
    setSavedAt(Date.now());
  }

  async function setSkipThisWeek(enabled: boolean) {
    if (streak === null) return;
    const previous = streak;
    setStreak({ ...streak, skipThisWeek: enabled });
    const { status, body } = await api.setSkipThisWeek(enabled);
    if (status !== 200 || body === null || !("currentStreak" in body)) {
      setStreak(previous);
      setSaveError("Couldn't update the streak freeze.");
      return;
    }
    setStreak(body as StreakState);
    setSavedAt(Date.now());
  }

  function previewVoice() {
    const synth = window.speechSynthesis;
    if (!synth) return;
    const utterance = new SpeechSynthesisUtterance("Hello Eva. Let's spell some words.");
    utterance.rate = draftRate;
    if (draftVoice !== null) {
      const v = voices.find((x) => x.voiceURI === draftVoice);
      if (v) utterance.voice = v;
    }
    synth.cancel();
    synth.speak(utterance);
  }

  async function handleSignOut() {
    setSignOutConfirmOpen(false);
    await logout();
    // AuthProvider will flip to 'unauthenticated' and the app re-renders
    // back to the login screen.
  }

  async function handleResetStats() {
    setDestructiveRunning("reset-stats");
    setSaveError(null);
    const { status } = await api.resetStats();
    setDestructiveRunning(null);
    setResetStatsConfirmOpen(false);
    if (status !== 200) {
      setSaveError("Couldn't reset stats. Try again.");
      return;
    }
    setDestructiveDone("reset-stats");
    // Streak toggle visibly resets — refresh local state.
    void load();
  }

  async function handleClearWords() {
    setDestructiveRunning("clear-words");
    setSaveError(null);
    const { status } = await api.clearWords();
    setDestructiveRunning(null);
    setClearWordsConfirmOpen(false);
    if (status !== 200) {
      setSaveError("Couldn't clear words. Try again.");
      return;
    }
    setDestructiveDone("clear-words");
  }

  if (loading) return <p className="parent-settings__loading">Loading…</p>;
  if (loadError !== null) {
    return (
      <div className="parent-settings__error">
        <p>{loadError}</p>
        <Button
          variant="secondary"
          onClick={() => {
            void load();
          }}
        >
          Try again
        </Button>
      </div>
    );
  }
  if (settings === null) return null;

  const hasUnsavedDrafts =
    draftVoice !== settings.speechVoice || draftRate !== settings.speechRate;

  return (
    <div className="parent-settings">
      <section className="parent-settings__section">
        <h2 className="parent-settings__heading">Speech</h2>

        <label className="parent-settings__field">
          <span className="parent-settings__field-label">Voice</span>
          <select
            className="parent-settings__select"
            value={draftVoice ?? ""}
            onChange={(e) => setDraftVoice(e.target.value === "" ? null : e.target.value)}
          >
            <option value="">Device default</option>
            {voices.map((v) => (
              <option key={v.voiceURI} value={v.voiceURI}>
                {v.name} ({v.lang})
              </option>
            ))}
          </select>
          <span className="parent-settings__hint">
            iPad/iPhone voices are limited by what iOS has installed. For a
            much more natural-sounding UK voice, open <strong>Settings →
            Accessibility → Spoken Content → Voices → English → English
            (United Kingdom)</strong> and download a <em>Siri Voice</em>
            (Voice 3 or Voice 4 are excellent) or a <em>Premium</em>
            version of Serena. Once downloaded it&rsquo;ll appear in this
            dropdown automatically.
          </span>
        </label>

        <label className="parent-settings__field">
          <span className="parent-settings__field-label">
            Speech rate ({draftRate.toFixed(2)}×)
          </span>
          <input
            type="range"
            min={RATE_MIN}
            max={RATE_MAX}
            step={RATE_STEP}
            value={draftRate}
            onChange={(e) => setDraftRate(Number(e.target.value))}
            className="parent-settings__range"
          />
          <span className="parent-settings__hint">
            Default 0.90× — a touch slower than the system default for clearer letter sounds.
          </span>
        </label>

        <div className="parent-settings__row-actions">
          <Button variant="secondary" size="small" onClick={previewVoice}>
            Preview
          </Button>
          <Button size="small" onClick={() => void saveDrafts()} disabled={saving || !hasUnsavedDrafts}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </section>

      <section className="parent-settings__section">
        <h2 className="parent-settings__heading">Audio</h2>
        <label className="parent-settings__toggle">
          <input
            type="checkbox"
            checked={!settings.audioEnabled}
            onChange={(e) => void setAudioEnabled(!e.target.checked)}
          />
          <span>Mute all sound</span>
        </label>
        <p className="parent-settings__hint">
          Eva won&rsquo;t hear words spoken aloud while muted — useful in shared spaces.
        </p>
      </section>

      <section className="parent-settings__section">
        <h2 className="parent-settings__heading">Streak</h2>
        {streak === null ? (
          <p className="parent-settings__hint">Couldn&rsquo;t load streak settings.</p>
        ) : (
          <>
            <label className="parent-settings__toggle">
              <input
                type="checkbox"
                checked={streak.skipThisWeek}
                onChange={(e) => void setSkipThisWeek(e.target.checked)}
              />
              <span>Skip this week</span>
            </label>
            <p className="parent-settings__hint">
              Freezes Eva&rsquo;s streak for the rest of the practice week —
              good for school holidays or sick days. Clears automatically next
              Tuesday.
            </p>
          </>
        )}
      </section>

      {saveError !== null && <p className="parent-settings__save-error">{saveError}</p>}
      {savedAt !== null && saveError === null && destructiveDone === null && (
        <p className="parent-settings__save-ok">Saved.</p>
      )}
      {destructiveDone === "reset-stats" && (
        <p className="parent-settings__save-ok">
          Eva&rsquo;s history, streaks, and stickers have been reset.
        </p>
      )}
      {destructiveDone === "clear-words" && (
        <p className="parent-settings__save-ok">
          All words have been cleared. Add a new week from the Words tab.
        </p>
      )}

      <section className="parent-settings__section parent-settings__section--danger">
        <h2 className="parent-settings__heading">Reset &amp; clear</h2>
        <p className="parent-settings__hint">
          Two separate actions. <em>Reset stats</em> wipes Eva&rsquo;s
          gameplay history but keeps her spelling words. <em>Clear words</em>
          deletes the current week and every past week but keeps her stats.
        </p>
        <div className="parent-settings__danger-actions">
          <Button
            variant="danger"
            size="small"
            onClick={() => setResetStatsConfirmOpen(true)}
          >
            Reset stats
          </Button>
          <Button
            variant="danger"
            size="small"
            onClick={() => setClearWordsConfirmOpen(true)}
          >
            Clear words
          </Button>
        </div>
      </section>

      <section className="parent-settings__section parent-settings__section--danger">
        <h2 className="parent-settings__heading">This device</h2>
        <p className="parent-settings__hint">
          Signing out clears the login on this device. Next time someone opens the app here,
          they&rsquo;ll need to enter the password again.
        </p>
        <Button variant="danger" size="small" onClick={() => setSignOutConfirmOpen(true)}>
          Sign out this device
        </Button>
      </section>

      <Modal
        open={signOutConfirmOpen}
        onClose={() => setSignOutConfirmOpen(false)}
        titleId="signout-title"
      >
        <h2 id="signout-title" className="parent-settings__modal-title">
          Sign out this device?
        </h2>
        <p className="parent-settings__modal-body">
          Eva will be asked for the password the next time the app opens here.
        </p>
        <div className="parent-settings__modal-actions">
          <Button variant="secondary" onClick={() => setSignOutConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void handleSignOut()}>
            Sign out
          </Button>
        </div>
      </Modal>

      <Modal
        open={resetStatsConfirmOpen}
        onClose={() => {
          if (destructiveRunning !== "reset-stats") setResetStatsConfirmOpen(false);
        }}
        titleId="reset-stats-title"
      >
        <h2 id="reset-stats-title" className="parent-settings__modal-title">
          Reset all stats?
        </h2>
        <p className="parent-settings__modal-body">
          This will wipe Eva&rsquo;s session history, streaks, and stickers.
          Her spelling words will stay. This can&rsquo;t be undone.
        </p>
        <div className="parent-settings__modal-actions">
          <Button
            variant="secondary"
            onClick={() => setResetStatsConfirmOpen(false)}
            disabled={destructiveRunning === "reset-stats"}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleResetStats()}
            disabled={destructiveRunning === "reset-stats"}
          >
            {destructiveRunning === "reset-stats" ? "Resetting…" : "Yes, reset stats"}
          </Button>
        </div>
      </Modal>

      <Modal
        open={clearWordsConfirmOpen}
        onClose={() => {
          if (destructiveRunning !== "clear-words") setClearWordsConfirmOpen(false);
        }}
        titleId="clear-words-title"
      >
        <h2 id="clear-words-title" className="parent-settings__modal-title">
          Clear all words?
        </h2>
        <p className="parent-settings__modal-body">
          This deletes the current week and every past week. Eva will see the
          first-launch screen until you add new words. Her stats and stickers
          will stay. This can&rsquo;t be undone.
        </p>
        <div className="parent-settings__modal-actions">
          <Button
            variant="secondary"
            onClick={() => setClearWordsConfirmOpen(false)}
            disabled={destructiveRunning === "clear-words"}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleClearWords()}
            disabled={destructiveRunning === "clear-words"}
          >
            {destructiveRunning === "clear-words" ? "Clearing…" : "Yes, clear all words"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
